import { AsyncSelectOptions } from "@/components/ui/FormFields/AsyncSelectOptions";
import {
  FilterSelector,
  type FilterOption,
} from "@/components/ui/FilterSelector/FilterSelector";
import {
  AppModal,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { formatDate } from "@/lib/utils/format";
import { Check, SlidersHorizontal, X } from "lucide-react";
import { useMemo, useState } from "react";

type Filters = Partial<Record<string, string[]>>;

type FilterBarProps = {
  filterOptions: FilterOption[];
  selectedFilters: Filters;
  onAddFilter: (key: string, value: string) => void;
  onRemoveFilter: (key: string, value: string) => void;
  onClearFilters?: () => void | Promise<void>;
  quickFilterValues?: string[];
};

export function QuickFilter({
  filterOption,
  selectedValues,
  onAddFilter,
  onRemoveFilter,
}: {
  filterOption: FilterOption;
  selectedValues: string[];
  onAddFilter: (key: string, value: string) => void;
  onRemoveFilter: (key: string, value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const input = filterOption.input;
  const items =
    input.type === "select" || input.type === "searchable-select"
      ? input.items
      : [];
  const asyncInput =
    input.type === "searchable-select" && input.onSearchChange
      ? input
      : undefined;
  const visibleItems = items.filter((item) => {
    if (asyncInput?.isItemsLoading || asyncInput?.isItemsError) return false;
    if (!asyncInput?.search?.trim()) return true;
    return (
      asyncInput.matchingValues?.includes(item.value) ||
      `${item.label} ${item.searchText ?? ""}`
        .toLowerCase()
        .includes(asyncInput.search.trim().toLowerCase())
    );
  });
  const groupedItems = Array.from(
    visibleItems.reduce((groups, item) => {
      const group = item.group ?? "";
      const groupItems = groups.get(group) ?? [];
      groupItems.push(item);
      groups.set(group, groupItems);
      return groups;
    }, new Map<string, typeof visibleItems>()),
  );
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    asyncInput?.onOpenChange?.(nextOpen);
  };

  return (
    <Popover modal open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="h-8 rounded-full px-3 text-xs font-medium"
        >
          {filterOption.label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <Command shouldFilter={!asyncInput}>
          {input.type === "searchable-select" ? (
            <CommandInput
              value={asyncInput?.search}
              onValueChange={asyncInput?.onSearchChange}
              placeholder={`Search ${filterOption.label.toLowerCase()}...`}
            />
          ) : null}
          <CommandList>
            {!asyncInput?.isItemsLoading && !asyncInput?.isItemsError ? (
              <CommandEmpty>No options found.</CommandEmpty>
            ) : null}
            {groupedItems.map(([group, groupItems]) => {
              const content = groupItems.map((item) => {
                const isSelected = selectedValues.includes(item.value);
                return (
                  <CommandItem
                    key={item.value}
                    value={`${item.label} ${item.searchText ?? ""} ${item.value}`}
                    onSelect={() => {
                      if (isSelected) {
                        onRemoveFilter(filterOption.value, item.value);
                      } else {
                        onAddFilter(filterOption.value, item.value);
                      }
                    }}
                  >
                    <Check
                      className={isSelected ? "opacity-100" : "opacity-0"}
                    />
                    {item.label}
                  </CommandItem>
                );
              });
              return group ? (
                <CommandGroup key={group} heading={group}>
                  {content}
                </CommandGroup>
              ) : (
                content
              );
            })}
            {asyncInput ? <AsyncSelectOptions {...asyncInput} /> : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function FilterBar({
  filterOptions,
  selectedFilters,
  onAddFilter,
  onRemoveFilter,
  onClearFilters,
  quickFilterValues = [],
}: FilterBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const activeFilters = useMemo(
    () =>
      Object.entries(selectedFilters).flatMap(([key, values]) => {
        const option = filterOptions.find(
          (candidate) =>
            candidate.value === key ||
            (candidate.input.type === "date" &&
              candidate.input.conditionOptions?.some(
                (condition) => condition.value === key,
              )),
        );
        const items =
          option?.input.type === "select" ||
          option?.input.type === "searchable-select"
            ? option.input.items
            : [];
        const condition =
          option?.input.type === "date"
            ? option.input.conditionOptions?.find((item) => item.value === key)
            : undefined;
        const label = condition
          ? `${option?.label} (${condition.label})`
          : (option?.label ?? key);
        return (values ?? []).map((value) => ({
          key,
          value,
          label: `${label}: ${option?.input.type === "date" ? (formatDate(value) ?? value) : (items.find((item) => item.value === value)?.label ?? value)}`,
        }));
      }),
    [filterOptions, selectedFilters],
  );
  const quickFilters = quickFilterValues
    .map((value) => filterOptions.find((option) => option.value === value))
    .filter(
      (option): option is FilterOption =>
        option !== undefined &&
        (option.input.type === "select" ||
          option.input.type === "searchable-select"),
    );

  const clearAppliedFilters = () => {
    if (onClearFilters) {
      void onClearFilters();
      return;
    }
    activeFilters.forEach(({ key, value }) => {
      onRemoveFilter(key, value);
    });
  };
  return (
    <>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {quickFilters.map((filterOption) => (
          <QuickFilter
            key={filterOption.value}
            filterOption={filterOption}
            selectedValues={selectedFilters[filterOption.value] ?? []}
            onAddFilter={onAddFilter}
            onRemoveFilter={onRemoveFilter}
          />
        ))}
        <div
          className={cn(
            "relative inline-flex",
            activeFilters.length > 0 &&
              "h-8 overflow-hidden rounded-full border",
          )}
        >
          <Button
            type="button"
            variant={activeFilters.length > 0 ? "ghost" : "outline"}
            className={cn(
              "h-8 px-3 text-xs font-medium",
              activeFilters.length > 0 ? "rounded-none" : "rounded-full",
            )}
            onClick={() => {
              setIsOpen(true);
            }}
          >
            <SlidersHorizontal />
            All filters
            {activeFilters.length > 0 ? (
              <span className="bg-destructive text-destructive-foreground min-w-5 rounded-full px-1 py-0.5 text-center text-xs leading-none">
                {activeFilters.length}
              </span>
            ) : null}
          </Button>
          {activeFilters.length > 0 ? (
            <Button
              type="button"
              variant="ghost"
              aria-label="Clear filters"
              className="h-8 w-8 rounded-none border-l p-0"
              onClick={clearAppliedFilters}
            >
              <X />
            </Button>
          ) : null}
        </div>
      </div>

      <AppModal
        open={isOpen}
        onOpenChange={setIsOpen}
        title="All filters"
        overlayClassName="z-[108]"
        className="z-[109]"
        size="lg"
        bodyContentClassName="p-0"
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            <Button
              type="button"
              variant="link"
              disabled={activeFilters.length === 0}
              onClick={clearAppliedFilters}
            >
              Clear all
            </Button>
            <Button
              type="button"
              variant="foreground"
              onClick={() => {
                setIsOpen(false);
              }}
            >
              Done
            </Button>
          </div>
        }
      >
        <div className="space-y-4 p-4">
          {activeFilters.length > 0 ? (
            <FilterSelector
              filterOptions={filterOptions}
              selectedFilters={selectedFilters}
              onAddFilter={onAddFilter}
              onRemoveFilter={onRemoveFilter}
              badgesOnly
            />
          ) : null}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {filterOptions.map((filterOption) => {
              const values = selectedFilters[filterOption.value] ?? [];
              return (
                <FilterSelector
                  key={filterOption.value}
                  filterOptions={[filterOption]}
                  selectedFilters={
                    values.length ? { [filterOption.value]: values } : {}
                  }
                  onAddFilter={onAddFilter}
                  onRemoveFilter={onRemoveFilter}
                  showHelperText={false}
                  selectStyle={null}
                  fullWidth
                  hidePrimarySelect
                  showActiveFilterBadges={false}
                />
              );
            })}
          </div>
        </div>
      </AppModal>
    </>
  );
}
